import { PriceItem } from "@silversea-ssc/ui-components-react";
import {
  perGuestLabel,
  priceFromBottomLabel,
  transformPriceFrom
} from "components/Prices/utils";
import React, { useMemo } from "react";
import { EnhancedAvailablePriceWithFareCode } from "src/ssc-api/prices/types";
import { isPresent } from "src/utils/checks";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import * as styles from "./StickyCruisePrice.module.scss";
import { useLingui } from "@lingui/react/macro";
import { useRemotePaymentTermsPerCruise } from "src/ssc-api/useRemotePaymentTerms";
import clsx from "clsx";

type Props = {
  price: EnhancedAvailablePriceWithFareCode | undefined;
  cruiseCode: string;
  departureDate?: string;
};

const StickyCruisePrice: React.FC<Props> = ({
  price,
  cruiseCode,
  departureDate
}) => {
  const geolocation = useGeolocation();
  const { t } = useLingui();
  const isFreeCancellationExperimentActive = false;

  const remotePaymentTerms = useRemotePaymentTermsPerCruise(
    price ? [price.webFareCode] : [],
    cruiseCode,
    true
  );

  const transformedPrice = transformPriceFrom(
    t,
    price,
    geolocation.defaultCurrency,
    true,
    "only-from"
  );
  const isWaitlist = price?.type == "waitlist";

  const cancellationPeriods =
    remotePaymentTerms.length > 0
      ? remotePaymentTerms[0].data?.cancellationPeriods
      : null;

  const depositType =
    remotePaymentTerms.length > 0
      ? remotePaymentTerms[0].data?.depositType
      : null;

  const currentCancellationPeriod =
    remotePaymentTerms.length > 0
      ? remotePaymentTerms[0].data?.currentCancellationPeriod
      : null;

  const priceBottomLabel = useMemo(() => {
    return priceFromBottomLabel(
      price,
      departureDate,
      isFreeCancellationExperimentActive,
      depositType,
      cancellationPeriods
    );
  }, [departureDate, cancellationPeriods, isFreeCancellationExperimentActive]);

  if (!isPresent(price)) return null;

  return (
    <div className={styles.container}>
      <PriceItem
        price={transformedPrice}
        variant="small"
        layout="row"
        showFareLabel
        chipBackgroundColor="neutral05"
        perGuestLabel={t(perGuestLabel())}
        priceType="primary"
      />
      {!isWaitlist && (
        <div
          className={clsx(styles.bottomLabel, {
            [styles.bottomLabelBold]:
              isFreeCancellationExperimentActive &&
              currentCancellationPeriod &&
              currentCancellationPeriod.type !== "NonRefundable"
          })}
        >
          {t(priceBottomLabel)}
        </div>
      )}
    </div>
  );
};

export default StickyCruisePrice;
